import React, { useEffect, useState } from 'react';
import { Placement } from 'react-joyride';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
  useLocation
} from 'react-router-dom';

import IcProFoodora from '@deliveryhero/pd-cookbook/components/Icons/brand/ic-pro-foodora';
import IcAllowance from '@deliveryhero/pd-cookbook/components/Icons/IcAllowance';
import IcBuilding from '@deliveryhero/pd-cookbook/components/Icons/IcBuilding';
import IcChevronDown from '@deliveryhero/pd-cookbook/components/Icons/IcChevronDown';
import IcChevronUp from '@deliveryhero/pd-cookbook/components/Icons/IcChevronUp';
import IcGift from '@deliveryhero/pd-cookbook/components/Icons/IcGift';
import IcHome from '@deliveryhero/pd-cookbook/components/Icons/IcHome';
import IcPro from '@deliveryhero/pd-cookbook/components/Icons/IcPro';
import IcReport from '@deliveryhero/pd-cookbook/components/Icons/IcReport';
import IcThumbsUp from '@deliveryhero/pd-cookbook/components/Icons/IcThumbsUp';
import IcVouchers from '@deliveryhero/pd-cookbook/components/Icons/IcVouchers';
import {
  Box,
  Collapse,
  Drawer,
  Link as MuiLink,
  List,
  ListItem,
  ListItemButton as MuiListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import CookieService from 'services/cookie';
import { FTUE_FLAG_TYPES } from 'services/cookie/types';
import { AppRoute, AppRouteId } from 'share/types/routes';
import { useConfiguration } from 'src/contexts/country-config';
import { useFeatureFlags } from 'src/contexts/feature-flags';
import isFoodora from 'src/utils/is-foodora/is-foodora';
import { FeatureFlag } from 'utils/constants';

import { Logo } from 'components/Logo';
import { BrandCodes } from 'components/Theme/types';

import {
  IconWrapperProps,
  Label,
  ListItemButtonProps,
  ListItemLinkProps,
  Props,
  RouteDef
} from './types';

import '@deliveryhero/pd-cookbook/assets/scss/helpers/_utility-classes.scss';

const ListItemButton = styled(MuiListItemButton, {
  shouldForwardProp: (prop) => prop !== 'inset'
})<ListItemButtonProps>(({ theme, selected, inset }) => ({
  minHeight: inset ? 50 : 60,
  paddingLeft: inset ? 12 : 0,
  color: selected
    ? theme.palette.interaction.primary
    : theme.palette.neutral.primary,
  '&.Mui-selected': {
    backgroundColor: theme.palette.interaction.primaryFeedback
  },
  '&.Mui-selected:hover': {
    backgroundColor: theme.palette.neutral.surface
  },
  '&:hover': {
    backgroundColor: theme.palette.neutral.surface
  }
}));

const ListIconStyle = { padding: '12px 12px 12px 28px', minWidth: '24px' };
// TODO update this to Label/label-md
const fontLabel = { fontSize: '14px', fontWeight: 600, lineHeight: '21px' };

const SelectedMark = () => (
  <Box
    component={'span'}
    sx={{
      width: 4,
      height: 24,
      backgroundColor: 'interaction.primary',
      position: 'absolute',
      left: 0,
      borderRadius: 1
    }}
  />
);

const ListItemLink = ({
  icon: Icon,
  iconProps,
  label,
  to,
  selected,
  inset,
  mark,
  fullPath
}: ListItemLinkProps) => {
  const theme = useTheme();
  const interactionPrimary = theme.palette.interaction.primary;
  const neutralPrimary = theme.palette.neutral.primary;
  const renderLink = React.useMemo(
    () =>
      React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'>>(
        function Link(itemProps, ref) {
          return fullPath ? (
            <MuiLink
              href={to}
              ref={ref}
              {...itemProps}
              role={undefined}
              target="_blank"
              rel="noreferrer"
            />
          ) : (
            <RouterLink to={to} ref={ref} {...itemProps} role={undefined} />
          );
        }
      ),
    [fullPath, to]
  );

  return (
    <ListItem disablePadding component={renderLink}>
      <ListItemButton
        selected={selected}
        inset={inset}
        sx={{ padding: '0 28px 0px 0px' }}
      >
        {mark && <SelectedMark />}
        {Icon && (
          <ListItemIcon sx={ListIconStyle}>
            <Icon
              fill={selected ? 'interaction-primary' : 'neutral-primary'}
              style={{
                fill: iconProps?.fill
                  ? iconProps?.fill
                  : selected
                    ? interactionPrimary
                    : neutralPrimary
              }}
              {...iconProps}
            />
          </ListItemIcon>
        )}
        <ListItemText
          disableTypography
          inset={inset}
          primary={label}
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '21px',
            color: selected ? interactionPrimary : neutralPrimary
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const IconWrapper = ({ tag: Tag, active }: IconWrapperProps) => (
  <Tag fill={active ? 'interaction-primary' : 'neutral-primary'} />
);

export default function NaviDrawer({
  width,
  exclude = [],
  voucherShopUrl,
  handleShowTour,
  handleTourActive,
  isVerified,
  setSteps,
  showTour
}: Props) {
  const theme = useTheme();
  const featureFlags = useFeatureFlags();
  const interactionPrimary = theme.palette.interaction.primary;
  const neutralPrimary = theme.palette.neutral.primary;

  const routes: RouteDef[] = [
    {
      id: 'Index',
      label: 'Dashboard',
      path: AppRoute.Index,
      icon: IcHome
    },
    {
      id: 'Company',
      label: 'Company',
      icon: IcBuilding,
      children: [
        {
          id: 'Employee',
          label: 'Employees',
          path: AppRoute.Employee
        },
        {
          id: 'Department',
          label: 'Departments',
          path: AppRoute.Department
        },
        {
          id: 'Location',
          label: 'Locations',
          path: AppRoute.Location
        },
        {
          id: 'ExpenseCode',
          label: 'Expense codes',
          path: AppRoute.ExpenseCode
        },
        {
          id: 'CostCenter',
          label: 'Cost centers',
          path: AppRoute.CostCenter
        }
      ]
    },
    {
      id: 'Allowance',
      label: 'Allowance',
      path: AppRoute.Allowance,
      icon: IcAllowance
    },
    {
      id: 'Subscription',
      label: 'Subscription',
      path: AppRoute.Subscription,
      icon: isFoodora() ? IcProFoodora : IcPro,
      iconProps: {
        fill: '#FF2B85'
      }
    },
    {
      id: 'Vouchers',
      label: Label.Vouchers,
      path: voucherShopUrl,
      icon: IcVouchers
    },
    {
      id: 'Report',
      label: 'Reports',
      path: AppRoute.Report,
      icon: IcReport
    },
    {
      id: 'UpgradeAccount',
      label: 'Upgrade Account',
      path: AppRoute.UpgradeAccount,
      icon: IcThumbsUp
    }
  ];

  const routes_ftue: RouteDef[] = [
    routes[1],
    {
      id: 'Allowance',
      label: 'Allowance',
      path: AppRoute.Allowance,
      icon: IcAllowance
    },
    {
      id: 'Subscription',
      label: 'Subscription',
      path: AppRoute.Subscription,
      icon: isFoodora() ? IcProFoodora : IcPro,
      iconProps: {
        fill: '#FF2B85'
      }
    },
    {
      id: 'Report',
      label: 'Reports',
      path: AppRoute.Report,
      icon: IcReport
    }
  ];

  if (featureFlags(FeatureFlag.GiftCard)) {
    routes.splice(3, 0, {
      id: 'GiftCard',
      label: 'Gift Card',
      path: AppRoute.GiftCard,
      icon: IcGift
    });

    routes_ftue.splice(2, 0, {
      id: 'GiftCard',
      label: 'Gift Card',
      path: AppRoute.GiftCard,
      icon: IcGift
    });
  }

  const available_routes = routes.filter((r) => {
    if (r.children) {
      r.children = r.children.filter((sr) => !exclude.some((e) => e === sr.id));
      return r.children.length == 0 ? false : true;
    } else {
      return !exclude.some((e) => e === r.id);
    }
  });

  const nested_routes = available_routes.filter((r) => r.children);
  const default_expand = nested_routes.reduce<
    Record<AppRouteId, boolean> | Record<string, never>
  >((acc, curr) => {
    acc[curr.id] = false;
    return acc;
  }, {});

  const [expand, setExpand] = useState<Record<string, boolean>>(default_expand);
  const locations = useLocation();

  const handleExpand = (id: AppRouteId) => {
    setExpand({
      ...expand,
      [id]: !expand[id]
    });
  };

  const isActive = (route: RouteDef) => {
    if (!route.children) {
      return route.path === locations.pathname;
    } else {
      return route.children.some((r) => r.path === locations.pathname);
    }
  };
  const countryConfig = useConfiguration();
  const brandCode = countryConfig.entity.code.toLowerCase() as BrandCodes;

  const steps = [
    {
      target: '.dashboard-spotlight-navi-drawer',
      content:
        'Manage all employee data, office locations, employee allowances, and gift cards here',
      disableBeacon: true,
      placement: 'right' as Placement
    },
    {
      target: '.dashboard-spotlight-orders-and-expense',
      content:
        'Track your employees’ monthly expenses and see the breakdown of total orders, expenses and average order expenses',
      placement: 'right' as Placement
    }
  ];

  function waitForElement(selector: string) {
    return new Promise((resolve) => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
      }
      new MutationObserver((_mutationRecords, observer) => {
        const element = document.querySelector(selector);
        if (element) {
          resolve(element);
          observer.disconnect();
        }
      }).observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    });
  }

  useEffect(() => {
    if (
      CookieService.isFirstTimeUser(FTUE_FLAG_TYPES.COMPANY_ADMIN_DASHBOARD) &&
      isVerified // enable ftue only if user can acess dashboard
    ) {
      handleShowTour && handleShowTour(true);
      setSteps && setSteps(steps);
    } else {
      handleShowTour && handleShowTour(false);
    }

    waitForElement('.dashboard-spotlight-navi-drawer').then(() => {
      handleTourActive && handleTourActive(true);
    });
  }, []);

  return (
    <Box component="nav">
      <Drawer
        sx={{
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box'
          }
        }}
        variant="persistent"
        anchor="left"
        open={true}
      >
        <Box
          component="div"
          display="flex"
          sx={{
            width: '80%',
            height: 'auto',
            margin: '32px auto 16px',
            justifyContent: 'center'
          }}
        >
          <Logo
            brandCode={brandCode}
            {...(brandCode == 'fo' && { transform: 'scale(0.85)' })}
          />
        </Box>

        <List>
          {showTour ? (
            <>
              <React.Fragment key="0">
                <ListItemLink
                  to={''}
                  label={routes[0].label}
                  icon={routes[0].icon}
                  selected={isActive(routes[0])}
                  mark={isActive(routes[0])}
                  iconProps={routes[0].iconProps}
                />
              </React.Fragment>
              <div className="dashboard-spotlight-navi-drawer">
                {routes_ftue.map((route, index) => (
                  <React.Fragment key={index}>
                    <ListItemLink
                      to={''}
                      label={route.label}
                      icon={route.icon}
                      selected={isActive(route)}
                      mark={isActive(route)}
                      iconProps={route.iconProps}
                      fullPath={route.label == Label.Vouchers}
                    />
                  </React.Fragment>
                ))}
              </div>
            </>
          ) : (
            <>
              {available_routes.map((route, index) => (
                <React.Fragment key={index}>
                  {route.children ? (
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleExpand(route.id)}
                        selected={isActive(route) && !expand[route.id]}
                        sx={{
                          padding: '0 28px 0px 0px',
                          color: isActive(route)
                            ? interactionPrimary
                            : neutralPrimary
                        }}
                      >
                        {isActive(route) && <SelectedMark />}
                        {route.icon && (
                          <ListItemIcon
                            sx={{
                              ...ListIconStyle,
                              fill: isActive(route)
                                ? interactionPrimary
                                : neutralPrimary
                            }}
                          >
                            <IconWrapper
                              tag={route.icon}
                              active={isActive(route)}
                            ></IconWrapper>
                          </ListItemIcon>
                        )}
                        <ListItemText
                          disableTypography
                          primary={route.label}
                          sx={fontLabel}
                        />
                        {expand[route.id] ? <IcChevronUp /> : <IcChevronDown />}
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    route.path && (
                      <ListItemLink
                        to={route.path}
                        label={route.label}
                        icon={route.icon}
                        selected={isActive(route)}
                        mark={isActive(route)}
                        iconProps={route.iconProps}
                        fullPath={route.label == Label.Vouchers}
                      />
                    )
                  )}

                  {route.children && (
                    <Collapse
                      in={expand[route.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {route.children.map(
                          (subroute, index) =>
                            subroute.path && (
                              <ListItemLink
                                key={index}
                                to={subroute.path}
                                label={subroute.label}
                                icon={subroute.icon}
                                selected={isActive(subroute)}
                                inset
                              />
                            )
                        )}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              ))}
            </>
          )}
        </List>
      </Drawer>
    </Box>
  );
}
